/**
 * Type Mappers
 * Convert between frontend types (camelCase) and backend DTOs (PascalCase)
 */

import type { WorkingPattern } from '@/types/timesheet';
import { DEFAULT_TIMESHEET_SETTINGS } from '@/types/timesheetSettings';
import type { 
  OrganisationTimesheetSettings,
  TimesheetDeadlineSettings,
  HoursLimitSettings,
  UtilisationSettings,
  PolicyViolationSettings,
  PolicyCheckConfig,
  PolicyLimits,
  PolicyCheckType,
  ApprovalScreenSettings,
  OvertimeSettings,
} from '@/types/timesheetSettings';
import type {
  OrganisationTimesheetSettingsDto,
  TimesheetDeadlineSettingsDto,
  HoursLimitSettingsDto,
  UtilisationSettingsDto,
  PolicyViolationSettingsDto,
  ApprovalScreenSettingsDto,
  WorkingPatternDto,
  EmployeeWorkingPatternDto,
  WorkingDaysDto,
  DailyHoursDto,
  CreateWorkingPatternDto,
  OvertimeSettingsDto,
} from '@/types/api';


/**
 * Convert frontend WorkingPattern to backend WorkingDaysDto
 */
export function toWorkingDaysDto(workingDays: WorkingPattern['workingDays']): WorkingDaysDto {
  return {
    monday: workingDays.monday,
    tuesday: workingDays.tuesday,
    wednesday: workingDays.wednesday,
    thursday: workingDays.thursday,
    friday: workingDays.friday,
    saturday: workingDays.saturday,
    sunday: workingDays.sunday,
  };
}

/**
 * Convert backend WorkingDaysDto to frontend workingDays
 */
export function fromWorkingDaysDto(dto: WorkingDaysDto): WorkingPattern['workingDays'] {
  return {
    monday: dto.monday,
    tuesday: dto.tuesday,
    wednesday: dto.wednesday,
    thursday: dto.thursday,
    friday: dto.friday,
    saturday: dto.saturday,
    sunday: dto.sunday,
  };
}

/**
 * Convert frontend WorkingPattern dailyHours to backend DailyHoursDto
 */
export function toDailyHoursDto(dailyHours: WorkingPattern['dailyHours']): DailyHoursDto | null {
  if (!dailyHours) return null;
  return {
    monday: dailyHours.monday,
    tuesday: dailyHours.tuesday,
    wednesday: dailyHours.wednesday,
    thursday: dailyHours.thursday,
    friday: dailyHours.friday,
    saturday: dailyHours.saturday,
    sunday: dailyHours.sunday,
  };
}

/**
 * Convert backend DailyHoursDto to frontend dailyHours
 */
export function fromDailyHoursDto(dto: DailyHoursDto | null | undefined): WorkingPattern['dailyHours'] | undefined {
  if (!dto) return undefined;
  return {
    monday: dto.monday,
    tuesday: dto.tuesday,
    wednesday: dto.wednesday,
    thursday: dto.thursday,
    friday: dto.friday,
    saturday: dto.saturday,
    sunday: dto.sunday,
  };
}

/**
 * Convert frontend WorkingPattern to backend CreateWorkingPatternDto
 */
export function toCreateWorkingPatternDto(pattern: WorkingPattern): CreateWorkingPatternDto {
  return {
    hoursPerWeek: pattern.hoursPerWeek,
    workingDays: toWorkingDaysDto(pattern.workingDays),
    dailyHours: toDailyHoursDto(pattern.dailyHours),
    effectiveFrom: new Date().toISOString(),
    notes: pattern.notes || null,
  };
}

/**
 * Convert backend WorkingPatternDto to frontend WorkingPattern
 */
export function fromWorkingPatternDto(dto: WorkingPatternDto): WorkingPattern {
  return {
    hoursPerWeek: dto.hoursPerWeek,
    workingDays: fromWorkingDaysDto(dto.workingDays),
    dailyHours: fromDailyHoursDto(dto.dailyHours),
    notes: dto.notes || undefined,
  };
}

/**
 * Convert backend EmployeeWorkingPatternDto to frontend WorkingPattern with metadata
 */
export function fromEmployeeWorkingPatternDto(dto: EmployeeWorkingPatternDto): {
  pattern: WorkingPattern;
  inherited: boolean;
  id: string;
} {
  return {
    pattern: fromWorkingPatternDto(dto),
    inherited: dto.inherited,
    id: dto.id,
  };
}


/**
 * Convert frontend TimesheetDeadlineSettings to backend DTO
 */
export function toTimesheetDeadlineSettingsDto(
  settings: TimesheetDeadlineSettings
): TimesheetDeadlineSettingsDto {
  return {
    deadlineDay: settings.deadlineDay,
    deadlineTime: settings.deadlineTime,
    gracePeriodDays: settings.gracePeriodDays,
    sendReminders: settings.sendReminders,
    reminderDaysBefore: settings.reminderDaysBefore,
    sendFeedReminders: settings.sendFeedReminders,
    enableCopyPreviousWeek: settings.enableCopyPreviousWeek,
    copyDescriptions: settings.copyDescriptions,
    copyOnlyBillable: settings.copyOnlyBillable,
    enableAutoSmartFill: settings.enableAutoSmartFill,
    enableAiInsights: settings.enableAiInsights,
    enableApprovalReminders: settings.enableApprovalReminders,
    approvalReminderDaysThreshold: settings.approvalReminderDaysThreshold,
    enableManagerDigest: settings.enableManagerDigest,
    digestDayOfWeek: settings.digestDayOfWeek,
  };
}

/**
 * Convert backend TimesheetDeadlineSettingsDto to frontend
 */
export function fromTimesheetDeadlineSettingsDto(
  dto: TimesheetDeadlineSettingsDto
): TimesheetDeadlineSettings {
  return {
    deadlineDay: dto.deadlineDay as 0 | 1 | 2 | 3 | 4 | 5 | 6,
    deadlineTime: dto.deadlineTime,
    gracePeriodDays: dto.gracePeriodDays,
    sendReminders: dto.sendReminders,
    sendFeedReminders: dto.sendFeedReminders ?? DEFAULT_TIMESHEET_SETTINGS.deadline.sendFeedReminders,
    reminderDaysBefore: dto.reminderDaysBefore,
    enableCopyPreviousWeek: dto.enableCopyPreviousWeek ?? DEFAULT_TIMESHEET_SETTINGS.deadline.enableCopyPreviousWeek,
    copyDescriptions: dto.copyDescriptions ?? DEFAULT_TIMESHEET_SETTINGS.deadline.copyDescriptions,
    copyOnlyBillable: dto.copyOnlyBillable ?? DEFAULT_TIMESHEET_SETTINGS.deadline.copyOnlyBillable,
    enableAutoSmartFill: dto.enableAutoSmartFill ?? DEFAULT_TIMESHEET_SETTINGS.deadline.enableAutoSmartFill,
    enableAiInsights: dto.enableAiInsights ?? DEFAULT_TIMESHEET_SETTINGS.deadline.enableAiInsights,
    enableApprovalReminders: dto.enableApprovalReminders ?? DEFAULT_TIMESHEET_SETTINGS.deadline.enableApprovalReminders,
    approvalReminderDaysThreshold: dto.approvalReminderDaysThreshold ?? DEFAULT_TIMESHEET_SETTINGS.deadline.approvalReminderDaysThreshold,
    enableManagerDigest: dto.enableManagerDigest ?? DEFAULT_TIMESHEET_SETTINGS.deadline.enableManagerDigest,
    digestDayOfWeek: (dto.digestDayOfWeek as 1 | 2 | 3 | 4 | 5 | 6 | 7) ?? DEFAULT_TIMESHEET_SETTINGS.deadline.digestDayOfWeek,
  };
}

/**
 * Convert frontend HoursLimitSettings to backend DTO
 */
export function toHoursLimitSettingsDto(
  settings: HoursLimitSettings
): HoursLimitSettingsDto {
  return {
    dailyHoursLimit: settings.dailyHoursLimit,
    weeklyHoursLimit: settings.weeklyHoursLimit,
    enforceHardLimits: settings.enforceHardLimits,
  };
}

/**
 * Convert backend HoursLimitSettingsDto to frontend
 */
export function fromHoursLimitSettingsDto(
  dto: HoursLimitSettingsDto
): HoursLimitSettings {
  return {
    dailyHoursLimit: dto.dailyHoursLimit,
    weeklyHoursLimit: dto.weeklyHoursLimit,
    enforceHardLimits: dto.enforceHardLimits,
  };
}

/**
 * Convert frontend UtilisationSettings to backend DTO
 */
export function toUtilisationSettingsDto(
  settings: UtilisationSettings
): UtilisationSettingsDto {
  return {
    targetUtilizationRate: settings.targetUtilisationRate,
    calculationMethod: settings.calculationMethod,
  };
}

/**
 * Convert backend UtilisationSettingsDto to frontend
 */
export function fromUtilisationSettingsDto(
  dto: UtilisationSettingsDto
): UtilisationSettings {
  return {
    targetUtilisationRate: dto.targetUtilizationRate ?? DEFAULT_TIMESHEET_SETTINGS.utilisation.targetUtilisationRate,
    calculationMethod: (dto.calculationMethod as 'billable-total' | 'billable-available' | 'billable-expected') ?? DEFAULT_TIMESHEET_SETTINGS.utilisation.calculationMethod,
  };
}

/**
 * Convert frontend ApprovalScreenSettings to backend DTO
 */
export function toApprovalScreenSettingsDto(
  settings: ApprovalScreenSettings
): ApprovalScreenSettingsDto {
  return {
    panelDefaultExpanded: settings.panelDefaultExpanded,
    minSeverityToShow: settings.minSeverityToShow,
  };
}

/**
 * Convert backend ApprovalScreenSettingsDto to frontend
 */
export function fromApprovalScreenSettingsDto(
  dto: ApprovalScreenSettingsDto | undefined
): ApprovalScreenSettings {
  return {
    panelDefaultExpanded: dto?.panelDefaultExpanded ?? false,
    minSeverityToShow: (dto?.minSeverityToShow as 'all' | 'medium' | 'high') ?? 'all',
  };
}

/**
 * Convert backend OvertimeSettingsDto to frontend OvertimeSettings
 */
export function fromOvertimeSettingsDto(
  dto: OvertimeSettingsDto | undefined
): OvertimeSettings {
  if (!dto) return DEFAULT_TIMESHEET_SETTINGS.overtime;
  return {
    enabled: dto.enabled ?? false,
    thresholds: {
      mode: (dto.thresholds?.mode as 'employee-pattern' | 'manual-only') ?? 'employee-pattern',
      thresholdScope: (dto.thresholds?.thresholdScope as 'unclassified-only' | 'total-hours') ?? 'unclassified-only',
    },
    types: [], // types are now fetched independently from the OvertimeTypes API
  };
}

/**
 * Convert frontend OrganisationTimesheetSettings to backend DTO
 */
export function toOrganisationTimesheetSettingsDto(
  settings: OrganisationTimesheetSettings,
  rowVersion?: string
): OrganisationTimesheetSettingsDto {
  // Legacy check keys that should not be persisted to the DB JSON
  const LEGACY_CHECK_KEYS: string[] = ['missing-hours', 'non-working-day'];

  // Convert policy violation checks to DTO format, filtering out legacy keys
  const checksDto = settings.policyViolations?.checks 
    ? Object.fromEntries(
        Object.entries(settings.policyViolations.checks)
          .filter(([key]) => !LEGACY_CHECK_KEYS.includes(key))
          .map(([key, value]) => [
            key,
            {
              enabled: value.enabled,
              severity: value.severity,
              threshold: value.threshold,
              enforcement: value.enforcement,
            }
          ])
      )
    : undefined;

  return {
    deadline: toTimesheetDeadlineSettingsDto(settings.deadline),
    hoursLimits: toHoursLimitSettingsDto(settings.hoursLimits),
    utilization: toUtilisationSettingsDto(settings.utilisation),
    policyViolations: {
      monitoringFrequency: settings.policyViolations?.monitoringFrequency ?? 'week',
      suppressDailyWhenWeeklyExceeded: settings.policyViolations?.suppressDailyWhenWeeklyExceeded ?? true,
      consolidateDailyWarnings: settings.policyViolations?.consolidateDailyWarnings ?? true,
      checks: checksDto,
      displayMode: settings.policyViolations?.displayMode ?? 'smart',
      showInfoAlerts: settings.policyViolations?.showInfoAlerts ?? true,
      preset: settings.policyViolations?.preset ?? 'balanced',
    },
    policyLimits: settings.policyLimits ? {
      legalMaxHoursPerDay: settings.policyLimits.legalMaxHoursPerDay,
      legalMaxHoursPerWeek: settings.policyLimits.legalMaxHoursPerWeek,
    } : undefined,
    approvalScreen: toApprovalScreenSettingsDto(settings.approvalScreen),
    overtime: {
      enabled: settings.overtime.enabled,
      thresholds: {
        mode: settings.overtime.thresholds.mode,
        thresholdScope: settings.overtime.thresholds.thresholdScope,
      },
    },
    weekStartDay: settings.weekStartDay,
    showRole: settings.showRole,
    requireRole: settings.requireRole,
    showCostCentre: settings.showCostCentre,
    requireCostCentre: settings.requireCostCentre,
    allowWeekendEntry: false,
    timezoneId: "UTC",
    descriptionType: settings.descriptionType,
    requireDescription: settings.requireDescription,
    minimumEntryDuration: settings.minimumEntryDuration,
    timeRounding: settings.timeRounding,
    allowNegativeHours: settings.allowNegativeHours,
    historicalEntryLimit: settings.historicalEntryLimit,
    allowFutureDatedEntries: settings.allowFutureDatedEntries,
    projectSelectionScope: settings.projectSelectionScope,
    showArchivedProjects: settings.showArchivedProjects,
    showProjectTimeRemaining: settings.showProjectTimeRemaining,
    onboardingCompletedAt: settings.onboardingCompletedAt ?? undefined,
    updatedAt: new Date().toISOString(),
    updatedBy: null,
    rowVersion: rowVersion || '',
  };
}

/**
 * Convert backend OrganisationTimesheetSettingsDto to frontend
 * Note: workingProfile is fetched separately from OrganisationWorkingPattern API
 * and merged in settingsRepository.getOrganisationTimesheetSettings()
 */
export function fromOrganisationTimesheetSettingsDto(
  dto: OrganisationTimesheetSettingsDto
): OrganisationTimesheetSettings & { rowVersion: string } {
  // Convert policy violation checks from DTO format to frontend format
  const checksFromDto = dto.policyViolations?.checks;
  const defaultChecks = DEFAULT_TIMESHEET_SETTINGS.policyViolations.checks;
  let checks: PolicyViolationSettings['checks'] = defaultChecks;

  if (checksFromDto) {
    const parsedChecks = Object.fromEntries(
      Object.entries(checksFromDto).map(([key, value]) => [
        key as PolicyCheckType,
        {
          enabled: value.enabled,
          severity: value.severity as 'high' | 'medium' | 'low',
          threshold: value.threshold ?? undefined,
          enforcement: (value.enforcement as 'warn' | 'block' | null | undefined) ?? undefined,
        } satisfies PolicyCheckConfig,
      ])
    ) as Partial<Record<PolicyCheckType, PolicyCheckConfig>>;

    const mergedChecks: PolicyViolationSettings['checks'] = {
      ...defaultChecks,
      ...(parsedChecks as Partial<PolicyViolationSettings['checks']>),
    };

    // Migrate legacy keys to mode-prefixed keys (backward compat for old DB records)
    const legacyMissingHours = parsedChecks['missing-hours'];
    if (legacyMissingHours) {
      if (!parsedChecks['daily-missing-hours']) mergedChecks['daily-missing-hours'] = legacyMissingHours;
      if (!parsedChecks['weekly-missing-hours']) mergedChecks['weekly-missing-hours'] = legacyMissingHours;
    }

    const legacyNonWorkingDay = parsedChecks['non-working-day'];
    if (legacyNonWorkingDay) {
      if (!parsedChecks['daily-non-working-day']) mergedChecks['daily-non-working-day'] = legacyNonWorkingDay;
      if (!parsedChecks['weekly-non-working-day']) mergedChecks['weekly-non-working-day'] = legacyNonWorkingDay;
    }

    checks = mergedChecks;
  }

  // Convert policy limits from DTO
  const policyLimits: PolicyLimits | undefined = dto.policyLimits
    ? {
        legalMaxHoursPerDay: dto.policyLimits.legalMaxHoursPerDay,
        legalMaxHoursPerWeek: dto.policyLimits.legalMaxHoursPerWeek,
      }
    : DEFAULT_TIMESHEET_SETTINGS.policyLimits;

  return {
    deadline: fromTimesheetDeadlineSettingsDto(dto.deadline),
    workingProfile: DEFAULT_TIMESHEET_SETTINGS.workingProfile,
    policyLimits,
    hoursLimits: fromHoursLimitSettingsDto(dto.hoursLimits),
    utilisation: fromUtilisationSettingsDto(dto.utilization),
    policyViolations: {
      monitoringFrequency: (dto.policyViolations?.monitoringFrequency as 'day' | 'week') ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.monitoringFrequency,
      suppressDailyWhenWeeklyExceeded: dto.policyViolations?.suppressDailyWhenWeeklyExceeded ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.suppressDailyWhenWeeklyExceeded,
      consolidateDailyWarnings: dto.policyViolations?.consolidateDailyWarnings ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.consolidateDailyWarnings,
      checks,
      displayMode: (dto.policyViolations?.displayMode as 'smart' | 'all' | 'critical-only') ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.displayMode,
      showInfoAlerts: dto.policyViolations?.showInfoAlerts ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.showInfoAlerts,
      preset: (dto.policyViolations?.preset as 'strict' | 'balanced' | 'lenient' | 'custom') ?? DEFAULT_TIMESHEET_SETTINGS.policyViolations.preset,
    },
    weekStartDay: (dto.weekStartDay ?? 1) as 0|1|2|3|4|5|6,
    showRole: dto.showRole ?? DEFAULT_TIMESHEET_SETTINGS.showRole,
    requireRole: dto.requireRole ?? DEFAULT_TIMESHEET_SETTINGS.requireRole,
    showCostCentre: dto.showCostCentre ?? DEFAULT_TIMESHEET_SETTINGS.showCostCentre,
    requireCostCentre: dto.requireCostCentre ?? DEFAULT_TIMESHEET_SETTINGS.requireCostCentre,
    // Settings now persisted via backend DTO (with fallback defaults for backward compat)
    descriptionType: (dto.descriptionType as 'row' | 'daily') ?? DEFAULT_TIMESHEET_SETTINGS.descriptionType,
    requireDescription: (dto.requireDescription as 'never' | 'always') ?? DEFAULT_TIMESHEET_SETTINGS.requireDescription,
    minimumEntryDuration: (dto.minimumEntryDuration as '6min' | '15min' | '30min' | '1hour') ?? DEFAULT_TIMESHEET_SETTINGS.minimumEntryDuration,
    timeRounding: (dto.timeRounding as 'none' | '15min' | '30min' | '1hour') ?? DEFAULT_TIMESHEET_SETTINGS.timeRounding,
    allowNegativeHours: dto.allowNegativeHours ?? DEFAULT_TIMESHEET_SETTINGS.allowNegativeHours,
    historicalEntryLimit: dto.historicalEntryLimit ?? DEFAULT_TIMESHEET_SETTINGS.historicalEntryLimit,
    allowFutureDatedEntries: (dto.allowFutureDatedEntries as 'no' | '1week' | '2weeks' | '4weeks' | 'unlimited') ?? DEFAULT_TIMESHEET_SETTINGS.allowFutureDatedEntries,
    projectSelectionScope: (dto.projectSelectionScope as 'all' | 'assigned') ?? DEFAULT_TIMESHEET_SETTINGS.projectSelectionScope,
    showArchivedProjects: dto.showArchivedProjects ?? DEFAULT_TIMESHEET_SETTINGS.showArchivedProjects,
    showProjectTimeRemaining: dto.showProjectTimeRemaining ?? DEFAULT_TIMESHEET_SETTINGS.showProjectTimeRemaining,
    onboardingCompletedAt: dto.onboardingCompletedAt ?? null,
    approvalScreen: fromApprovalScreenSettingsDto(dto.approvalScreen),
    overtime: fromOvertimeSettingsDto(dto.overtime),
    updatedAt:dto.updatedAt && dto.updatedAt!=="" ? new Date(dto.updatedAt) : new Date(),
    rowVersion: dto.rowVersion ?? '',
  };
}
